/**
 * @fileOverview What the camera is told to do. The player only says where to look, how close and
 * over how long; `LewViewHelpStage` animates it, and `services/viewHelp/camera.ts` has the math.
 */

import { FRAME_HEIGHT, FRAME_WIDTH, cameraFor, zoomFor } from "@/services/viewHelp/camera"
import { needsScroll, visibleRegion, type Rect } from "@/services/viewHelp/target"
import type { ViewHelpStep } from "@/services/viewHelp/types"
import { ACTION_CAMERA_MS, ACTION_ZOOM_CAP, PULL_BACK_MS, prefersReducedMotion } from "./timing"
import { Move, cameraMs, focusCap, focusRect, frameWindow, scaled } from "./state"

/**
 * The region the camera holds while a step's own actions run, or null to follow each action's target.
 *
 * A step that fills in several fields — date, times and project together — would otherwise send the
 * camera to each field in turn, then to the menu option, then back out to the step: four moves for
 * one idea. Held, the camera goes in once onto the whole group and the pointer works inside the view.
 */
let heldRegion: Rect | null = null

/** Points the camera at `rect` — null for the whole page — over `ms`, zooming no closer than `cap`. */
export function aimCamera(rect: Rect | null, ms: number, cap: number | null = null) {
  focusCap.value = cap
  cameraMs.value = ms
  focusRect.value = rect
}

/** Whether the camera is zoomed in past the whole page. */
export const isZoomedIn = () => zoomFor(focusRect.value, focusCap.value ?? undefined) > 1.01

/**
 * Pulls the camera back to the whole page if `el` is about to be scrolled into view and the camera is
 * zoomed in — so the scroll happens on its own, at the page's own scale, as an ordinary scroll.
 *
 * Scrolling while zoomed, or while the camera was also zooming, moved the picture two ways at once:
 * the page slid vertically inside a view that was zooming evenly, and the two together read as the
 * zoom going one way across and another way down. One motion at a time: out, scroll, in. When the
 * camera already shows the whole page there is nothing to pull back from, and no pause is spent.
 */
export async function pullBackBeforeScrolling(win: Window, el: HTMLElement, move: Move, needed = needsScroll(win, el)) {
  if (prefersReducedMotion() || !needed || !isZoomedIn()) return
  aimCamera(null, scaled(PULL_BACK_MS))
  await move.pause(cameraMs.value)
}

/** Whether a box is inside what the camera shows while it holds `heldRegion`. */
function inHeldView(box: Rect): boolean {
  if (!heldRegion) return false
  // At a fit of 1 the camera's numbers are in frame pixels, and what it shows does not depend on the fit.
  const camera = cameraFor(heldRegion, 1)
  const left = -camera.tx / camera.s
  const top = -camera.ty / camera.s
  const cx = box.left + box.width / 2
  const cy = box.top + box.height / 2
  return cx >= left && cx <= left + FRAME_WIDTH / camera.s && cy >= top && cy <= top + FRAME_HEIGHT / camera.s
}

/**
 * Turns the camera onto what the pointer is about to use, at the gentler working zoom — unless the
 * camera is holding a step's region and the target is already in view, in which case it stays put.
 */
export function lookAt(el: HTMLElement, animate: boolean) {
  const box = el.getBoundingClientRect()
  lookAtRect({ top: box.top, left: box.left, width: box.width, height: box.height }, animate)
}

/**
 * The same for a region that is not one element's box — the two ends of a drag together, which have
 * to be in shot at once or the reader watches the pointer leave the picture carrying something.
 */
export function lookAtRect(box: Rect, animate: boolean) {
  if (!animate || inHeldView(box)) return
  aimCamera(box, scaled(ACTION_CAMERA_MS), ACTION_ZOOM_CAP)
}

/**
 * Holds the camera on the whole page — for a step with `zoom: false`, from its scene's first action to
 * its landing. A hold on the whole frame makes `lookAt` leave the camera where it is for any target on
 * screen, so the pointer clicks without the camera diving onto what it clicks.
 */
export function holdOnPage() {
  heldRegion = { top: 0, left: 0, width: FRAME_WIDTH, height: FRAME_HEIGHT }
  focusCap.value = 1
  cameraMs.value = scaled(ACTION_CAMERA_MS)
}

/**
 * Frames a step's whole region before its actions run, and holds it there — see `heldRegion`. When
 * the region is not on screen yet the camera is left to follow the pointer instead, which will scroll
 * each target into view as it goes.
 */
export async function holdOnRegion(candidate: ViewHelpStep, move: Move) {
  const win = frameWindow.value
  if (!win || !candidate.anchor || prefersReducedMotion()) return
  const region = visibleRegion(win, candidate.anchor)
  if (!region) return
  heldRegion = region
  aimCamera(region, scaled(ACTION_CAMERA_MS))
  await move.pause(cameraMs.value)
}

/** Ends a hold: from here on the camera follows each action's target again. */
export function releaseHold() {
  heldRegion = null
}

/**
 * Follows a region that has moved under a step that is up — a quick glide, always at 1×, and at
 * whatever zoom cap the step landed with.
 */
export function followRegion(rect: Rect, ms: number) {
  cameraMs.value = ms
  focusRect.value = rect
}
